import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { SimpleForm, TopToolbar as Toolbar, useCreatePath, useRecordContext, useRedirect } from 'react-admin';

import { STATUS, UserRoleLabel } from 'src/constants';
import EditIcon from 'src/assets/Edit';
import { ShsButton, ShsSelectInput, StatusBadge, WithRole } from 'src/components/common';
import { sectionTitleStyle } from 'src/styles';
import { EmployeeFormFields } from 'src/constants/employee';
import PhotoAvatar from '../common/PhotoAvatar';
import axiosApiInstance from 'src/api/axiosConfig';
import ReActivateIcon from 'src/assets/ReActivate';
import { CheckIsEmployeeActive, isEmployeeInactive } from 'src/utils/employees/employee-helper';
import { canEditEmployeeRoles } from './EmployeeConstants';
import { UserRole } from 'src/auth/types';
import { EmployeeCenterDetailsType, EmployeeTypeSubjectName } from './types';

/**
 * Toolbar component for displaying employee information and managing their assigned centers
 */
const EmployeeTopToolbar: React.FC<{
    onCenterChange?: (centerId: string) => void;
    selectedCenterId?: number;
    isUserProfile?: boolean;
    onInactiveComputed?: (isInactive: boolean) => void;
}> = ({ onCenterChange, selectedCenterId, isUserProfile, onInactiveComputed }) => {
    const redirect = useRedirect();
    const createPath = useCreatePath();
    const record = useRecordContext();

    const [selectedDefaultCenterId, setSelectedDefaultCenterId] = useState<string>(selectedCenterId?.toString() || '');
    const [centersData, setCentersData] = useState<any[]>([]);

    const titleName = `${record?.[EmployeeFormFields.LAST_NAME] || ''} ${record?.[EmployeeFormFields.FIRST_NAME] || ''} ${
        record?.[EmployeeFormFields.PATRONYMIC] || ''
    }`;
    let employeeCenters: EmployeeCenterDetailsType[] = [];
    if (record?.[EmployeeFormFields.EMPLOYEES_CENTERS] && Array.isArray(record[EmployeeFormFields.EMPLOYEES_CENTERS])) {
        employeeCenters = record?.[EmployeeFormFields.EMPLOYEES_CENTERS] as EmployeeCenterDetailsType[];
    } else if (record?.[EmployeeFormFields.EMPLOYEES] && Array.isArray(record[EmployeeFormFields.EMPLOYEES])) {
        employeeCenters = record?.[EmployeeFormFields.EMPLOYEES] as EmployeeCenterDetailsType[];
    }

    const filteredCentersChoices = useMemo<{ id: string; name: string }[]>(
        () =>
            centersData
                .filter((value) => employeeCenters?.some((center: any) => center.educationCenterId === value.id))
                .map((value) => ({ id: value.id.toString(), name: value.name })),
        [centersData, employeeCenters]
    );

    const isInactiveEmployee = isEmployeeInactive(record as CheckIsEmployeeActive);

    useEffect(() => {
        if (onInactiveComputed) {
            onInactiveComputed(isInactiveEmployee);
        }
    }, [isInactiveEmployee]);

    /**
     * Redirects to the edit page for the current employee
     */
    const handleEditClick = () => {
        redirect(createPath({ resource: 'employees', type: 'edit', id: record?.id }));
    };

    /**
     * Fetches employee center data from the API and updates the state with the received data
     */
    useEffect(() => {
        axiosApiInstance
            .get('centers')
            .then((response) => {
                setCentersData(response.data);
            })
            .catch((e) => {
                throw e;
            });
    }, []);

    /**
     * Sets the default selected center ID and triggers the onCenterChange callback when filtered center choices are available
     */
    useEffect(() => {
        if (selectedDefaultCenterId) return;

        let defaultCenterId: string | undefined = undefined;
        if (selectedCenterId !== undefined) {
            defaultCenterId = selectedCenterId.toString();
        } else if (filteredCentersChoices && filteredCentersChoices.length > 0) {
            defaultCenterId = filteredCentersChoices[0].id.toString();
        }
        if (defaultCenterId !== undefined) {
            setSelectedDefaultCenterId(defaultCenterId);
            if (onCenterChange) {
                onCenterChange(defaultCenterId);
            }
        }
    }, [filteredCentersChoices, selectedCenterId, selectedDefaultCenterId, onCenterChange]);

    /**
     * Handles the change event for the selected education center
     */
    const handleCenterChange = (event: any) => {
        const selectedId = event.target.value;
        setSelectedDefaultCenterId(selectedId);
        if (onCenterChange) {
            onCenterChange(selectedId);
        }
    };

    const selectedRoles: UserRole[] = [];
    const selectedRolesCLNC: UserRole[] = [];
    const selectedSubjectNames: string[] = [];
    if (employeeCenters?.length > 0 && selectedDefaultCenterId) {
        const selectedEmployeeCenters = employeeCenters?.filter(
            (center) => center.educationCenterId?.toString() === selectedDefaultCenterId
        );
        selectedEmployeeCenters?.forEach((center: EmployeeCenterDetailsType) => {
            if (center?.roles?.length) {
                selectedRoles.push(...center.roles);
            }
            if (center?.rolesCLNC?.length) {
                selectedRolesCLNC.push(...center.rolesCLNC);
            }
            if (center?.subjectNames?.length) {
                selectedSubjectNames.push(
                    ...(center?.subjectNames?.map((subject: EmployeeTypeSubjectName) => subject.name) || [])
                );
            }
        });
    }

    return (
        <Toolbar>
            <Stack display="flex" flexDirection="row" width={1}>
                <Box mr={3}>
                    <PhotoAvatar
                        firstName={record?.[EmployeeFormFields.FIRST_NAME] || ''}
                        lastName={record?.[EmployeeFormFields.LAST_NAME] || ''}
                        url={record?.photo}
                        size="extra-large"
                    />
                </Box>

                <Stack sx={{ flex: '1 1 100%' }}>
                    <Box sx={sectionTitleStyle}>{titleName}</Box>
                    <Box whiteSpace="normal" sx={{ mt: 1.5 }}>
                        <StatusBadge
                            status={
                                !record?.active && !record?.restricted
                                    ? STATUS.INACTIVE
                                    : record?.active && !record?.restricted
                                      ? STATUS.ACTIVE
                                      : STATUS.SUSPENDED
                            }
                        />
                    </Box>
                    <Box mt={1.5}>
                        {filteredCentersChoices && filteredCentersChoices.length > 1 ? (
                            selectedDefaultCenterId ? (
                                <SimpleForm toolbar={false}>
                                    <ShsSelectInput
                                        source="selectedDefaultCenterId"
                                        choices={filteredCentersChoices}
                                        label="Освітній центр"
                                        defaultValue={selectedDefaultCenterId}
                                        onChange={handleCenterChange}
                                        sx={{ maxWidth: '1000px' }}
                                        helperText={false}
                                        required
                                        disabled={isUserProfile}
                                    />
                                </SimpleForm>
                            ) : (
                                <Box>Завантаження...</Box>
                            )
                        ) : filteredCentersChoices && filteredCentersChoices.length === 1 ? (
                            <Box
                                sx={{
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    wordWrap: 'break-word',
                                    whiteSpace: 'normal'
                                }}>
                                <span>{filteredCentersChoices[0]?.name}</span>
                            </Box>
                        ) : (
                            <Box>Працівник не задіяний в жодному освітньому центрі</Box>
                        )}
                    </Box>
                    {selectedRoles?.length > 0 && (
                        <Box sx={{ marginTop: 1 }}>
                            Штатний працівник:{' '}
                            <span style={{ fontWeight: 'bold' }}>
                                {Object.entries(UserRoleLabel)
                                    .filter(([id]) => selectedRoles?.includes(id as UserRole))
                                    .map(([_, name]) => name)
                                    .join(', ')}
                            </span>
                        </Box>
                    )}
                    {selectedRolesCLNC?.length > 0 && (
                        <Box sx={{ marginTop: 1 }}>
                            ЦПХ:&nbsp;
                            <span style={{ fontWeight: 'bold' }}>
                                {Object.entries(UserRoleLabel)
                                    .filter(([id]) => selectedRolesCLNC?.includes(id as UserRole))
                                    .map(([_, name]) => name)
                                    .join(', ')}
                                {selectedSubjectNames.length > 0 && ` - ${selectedSubjectNames.join(', ')}`}
                            </span>
                        </Box>
                    )}
                </Stack>
                <Box>
                    <WithRole roles={canEditEmployeeRoles}>
                        {!isUserProfile && (
                            <ShsButton
                                label={isInactiveEmployee ? 'Активувати' : 'Редагувати'}
                                styleType={isInactiveEmployee ? 'filled' : 'outline'}
                                icon={isInactiveEmployee ? <ReActivateIcon color="white" /> : <EditIcon />}
                                sx={{ height: '40px' }}
                                onClick={handleEditClick}
                            />
                        )}
                    </WithRole>
                </Box>
            </Stack>
        </Toolbar>
    );
};

export default EmployeeTopToolbar;
